import React, { useState, useRef } from "react";
import { View, Image, StyleSheet, ScrollView, TouchableOpacity, Dimensions, ActivityIndicator } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useTheme } from "../../../context/ThemeContext";

const { width: screenWidth } = Dimensions.get("window");

export const ImageSlider = ({ images = [], height = 300 }) => {
	const theme = useTheme();
	const styles = useStyles(height);
	const [activeIndex, setActiveIndex] = useState(0);
	const [loading, setLoading] = useState(true);
	const scrollViewRef = useRef(null);

	const handleScroll = (event) => {
		const contentOffsetX = event.nativeEvent.contentOffset.x;
		const newIndex = Math.round(contentOffsetX / screenWidth);
		if (newIndex !== activeIndex) {
			setActiveIndex(newIndex);
		}
	};

	const handlePressNext = () => {
		if (activeIndex < images.length - 1) {
			scrollViewRef.current?.scrollTo({
				x: (activeIndex + 1) * screenWidth,
				animated: true,
			});
		}
	};

	const handlePressPrev = () => {
		if (activeIndex > 0) {
			scrollViewRef.current?.scrollTo({
				x: (activeIndex - 1) * screenWidth,
				animated: true,
			});
		}
	};

	const handleImageLoad = () => {
		setLoading(false);
	};

	if (!images.length) {
		return (
			<View style={[styles.container, styles.noImageContainer]}>
				<Ionicons name="image-outline" size={50} color={theme.colors.textSecondary} />
			</View>
		);
	}

	return (
		<View style={styles.container}>
			<ScrollView ref={scrollViewRef} horizontal pagingEnabled showsHorizontalScrollIndicator={false} onScroll={handleScroll} scrollEventThrottle={16}>
				{images.map((image, index) => (
					<View key={`image-${index}`} style={styles.slide}>
						{loading && (
							<View style={styles.loadingContainer}>
								<ActivityIndicator size="large" color={theme.colors.primary} />
							</View>
						)}
						<Image source={{ uri: image }} style={styles.image} resizeMode="cover" onLoad={handleImageLoad} />
					</View>
				))}
			</ScrollView>

			{/* Navigation arrows */}
			{images.length > 1 && (
				<>
					{activeIndex > 0 && (
						<TouchableOpacity style={[styles.navButton, styles.prevButton]} onPress={handlePressPrev}>
							<Ionicons name="chevron-back" size={24} color="#fff" />
						</TouchableOpacity>
					)}

					{activeIndex < images.length - 1 && (
						<TouchableOpacity style={[styles.navButton, styles.nextButton]} onPress={handlePressNext}>
							<Ionicons name="chevron-forward" size={24} color="#fff" />
						</TouchableOpacity>
					)}
				</>
			)}

			{/* Pagination dots */}
			{images.length > 1 && (
				<View style={styles.pagination}>
					{images.map((_, index) => (
						<View key={`dot-${index}`} style={[styles.paginationDot, index === activeIndex && styles.paginationDotActive]} />
					))}
				</View>
			)}
		</View>
	);
};

const useStyles = (height) => {
	const theme = useTheme();

	return StyleSheet.create({
		container: {
			height,
			width: "100%",
			position: "relative",
			backgroundColor: theme.colors.systemBackground.secondary,
		},
		slide: {
			width: screenWidth,
			height,
			justifyContent: "center",
			alignItems: "center",
		},
		image: {
			width: "100%",
			height: "100%",
		},
		loadingContainer: {
			position: "absolute",
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			justifyContent: "center",
			alignItems: "center",
			zIndex: 1,
		},
		navButton: {
			position: "absolute",
			top: "50%",
			transform: [{ translateY: -20 }],
			width: 40,
			height: 40,
			borderRadius: 20,
			backgroundColor: "rgba(0, 0, 0, 0.4)",
			justifyContent: "center",
			alignItems: "center",
			zIndex: 2,
		},
		prevButton: {
			left: 16,
		},
		nextButton: {
			right: 16,
		},
		pagination: {
			position: "absolute",
			bottom: 16,
			width: "100%",
			flexDirection: "row",
			justifyContent: "center",
			alignItems: "center",
		},
		paginationDot: {
			width: 8,
			height: 8,
			borderRadius: 4,
			backgroundColor: "rgba(255, 255, 255, 0.5)",
			marginHorizontal: 4,
		},
		paginationDotActive: {
			backgroundColor: "#fff",
			width: 10,
			height: 10,
			borderRadius: 5,
		},
		noImageContainer: {
			justifyContent: "center",
			alignItems: "center",
		},
	});
};
